import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { ScenarioSummary } from "./api";
import type { SimPlayStepResult } from "./api-types";

export type StepUiState = "pending" | "active" | "ok" | "fail";

type ReplayContextValue = {
  selected: ScenarioSummary | null;
  selectScenario: (scenario: ScenarioSummary) => void;
  stepStates: StepUiState[];
  setStepStates: Dispatch<SetStateAction<StepUiState[]>>;
  playing: boolean;
  setPlaying: (playing: boolean) => void;
  resetTimeline: () => void;
  replayOutcome: SimPlayStepResult | null;
  setReplayOutcome: (result: SimPlayStepResult | null) => void;
};

const ReplayContext = createContext<ReplayContextValue | null>(null);

export function ReplayProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<ScenarioSummary | null>(null);
  const [stepStates, setStepStates] = useState<StepUiState[]>([]);
  const [playing, setPlaying] = useState(false);
  const [replayOutcome, setReplayOutcome] = useState<SimPlayStepResult | null>(null);

  const selectScenario = useCallback((scenario: ScenarioSummary) => {
    setSelected(scenario);
    setStepStates((scenario.steps ?? []).map(() => "pending" as StepUiState));
    setPlaying(false);
    setReplayOutcome(null);
  }, []);

  const resetTimeline = useCallback(() => {
    setStepStates((selected?.steps ?? []).map(() => "pending"));
    setPlaying(false);
    setReplayOutcome(null);
  }, [selected]);

  const value = useMemo(
    () => ({
      selected,
      selectScenario,
      stepStates,
      setStepStates,
      playing,
      setPlaying,
      resetTimeline,
      replayOutcome,
      setReplayOutcome,
    }),
    [selected, selectScenario, stepStates, playing, resetTimeline, replayOutcome],
  );

  return <ReplayContext.Provider value={value}>{children}</ReplayContext.Provider>;
}

export function useReplay() {
  const ctx = useContext(ReplayContext);
  if (!ctx) {
    throw new Error("useReplay requires ReplayProvider");
  }
  return ctx;
}
